import { defaultSetupTemplates } from '../utils/constants';

/**
 * Custom hook for setup library CRUD operations.
 * Extracted from TradingJournal.js (Phase 4).
 *
 * Manages: getAllSetupsWithDetails, setup descriptions, rules, recommended confluences.
 */
const useSetupManagement = ({ customSetups, setupDetails, setSetupDetails }) => {

  // Get all setups as objects with details
  const getAllSetupsWithDetails = () => {
    const allSetups = [];

    // Add default setups
    defaultSetupTemplates.forEach(template => {
      const details = setupDetails[template.name] || {};
      allSetups.push({
        id: template.id,
        name: template.name,
        description: details.description || template.description || '',
        rules: details.rules || template.rules || [],
        recommendedConfluences: details.recommendedConfluences || template.recommendedConfluences || [],
        // Wave E.1 — implied confluences (auto-tagged on save). No template
        // defaults; the user opts in per-setup. Empty array when unset.
        impliedConfluences: details.impliedConfluences || [],
        isDefault: true
      });
    });

    // Add custom setups
    customSetups.forEach((name, index) => {
      const details = setupDetails[name] || {};
      allSetups.push({
        id: `custom-${index}`,
        name: name,
        description: details.description || '',
        rules: details.rules || [],
        recommendedConfluences: details.recommendedConfluences || [],
        impliedConfluences: details.impliedConfluences || [],
        isDefault: false
      });
    });

    return allSetups;
  };

  // Update setup description
  const updateSetupDescription = (setupName, description) => {
    setSetupDetails(prev => ({
      ...prev,
      [setupName]: {
        ...(prev[setupName] || {}),
        description
      }
    }));
  };

  // Add a rule to a setup
  const addSetupRule = (setupName, rule) => {
    if (!rule.trim()) return;
    setSetupDetails(prev => ({
      ...prev,
      [setupName]: {
        ...(prev[setupName] || {}),
        rules: [...(prev[setupName]?.rules || []), rule.trim()]
      }
    }));
  };

  // Remove a rule from a setup
  const removeSetupRule = (setupName, ruleIndex) => {
    setSetupDetails(prev => ({
      ...prev,
      [setupName]: {
        ...(prev[setupName] || {}),
        rules: (prev[setupName]?.rules || []).filter((_, idx) => idx !== ruleIndex)
      }
    }));
  };

  // Update a specific rule
  const updateSetupRule = (setupName, ruleIndex, newText) => {
    setSetupDetails(prev => ({
      ...prev,
      [setupName]: {
        ...(prev[setupName] || {}),
        rules: (prev[setupName]?.rules || []).map((rule, idx) =>
          idx === ruleIndex ? newText : rule
        )
      }
    }));
  };

  // Toggle a confluence for a setup's recommended confluences
  const toggleSetupConfluence = (setupName, confluenceId, defaultConfluences = []) => {
    setSetupDetails(prev => {
      const currentConfluences = prev[setupName]?.recommendedConfluences || defaultConfluences;
      const hasConfluence = currentConfluences.includes(confluenceId);

      return {
        ...prev,
        [setupName]: {
          ...(prev[setupName] || {}),
          recommendedConfluences: hasConfluence
            ? currentConfluences.filter(c => c !== confluenceId)
            : [...currentConfluences, confluenceId]
        }
      };
    });
  };

  // Wave E.1 — Toggle a confluence for a setup's IMPLIED confluences.
  // Distinct from `recommendedConfluences` (advisory). Implied chips are
  // auto-applied on every save of a trade with this setup, via
  // `applyImpliedConfluences` in the manual + JournifiSync save paths.
  // Defaults to empty array — no setup has implieds out of the box; the
  // user opts in per-setup.
  const toggleSetupImpliedConfluence = (setupName, confluenceId) => {
    setSetupDetails(prev => {
      const current = prev[setupName]?.impliedConfluences || [];
      const hasIt = current.includes(confluenceId);
      return {
        ...prev,
        [setupName]: {
          ...(prev[setupName] || {}),
          impliedConfluences: hasIt
            ? current.filter(c => c !== confluenceId)
            : [...current, confluenceId]
        }
      };
    });
  };

  return {
    getAllSetupsWithDetails,
    updateSetupDescription,
    addSetupRule,
    removeSetupRule,
    updateSetupRule,
    toggleSetupConfluence,
    toggleSetupImpliedConfluence
  };
};

export default useSetupManagement;
